import React from 'react';
import { SpaceLabel } from '../../types/TaskLabel';
import CustomTooltip from './Tooltip/CustomTooltip';
import { statusColorMap, statusTextColorMap } from '../../constants/design_system/statusColors';

export enum LabelChipType {
  Table = 'table',
  TableHeader = 'table_header',
  Dropdown = 'dropdown',
  TaskCard = 'task_card',
  Modal = 'modal',
}

export interface LabelChipProps {
  label: SpaceLabel;
  type: LabelChipType;
  removable?: boolean;
  onRemove?: (labelId: number) => void;
  onClick?: () => void;
}

const LabelChip: React.FC<LabelChipProps> = ({ label, type, removable = false, onRemove, onClick }) => {
  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.(label.id);
  };

  const maxWidth = getChipMaxWidth(type);
  const textWidth = getTextWidth(label.title, '12px Figtree');

  const chipStyle = {
    backgroundColor: statusColorMap[label.color],
    color: statusTextColorMap[label.color],
    maxWidth: maxWidth,
  };

  const chipContent = (
    <div
      style={chipStyle}
      className={`inline-flex items-center justify-center px-2  rounded text-sm font-normal ${
        onClick ? 'cursor-pointer hover:opacity-80' : ''
      }`}
      onClick={onClick}
    >
      <span className="whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: maxWidth }}>
        {label.title}
      </span>
      {removable && (
        <button
          onClick={handleRemove}
          className="ml-1 hover:bg-black hover:bg-opacity-20 rounded-full w-4 h-4 flex items-center justify-center text-xs"
          aria-label={`Remove ${label.title}`}
        >
          ×
        </button>
      )}
    </div>
  );

  // Show tooltip if text is truncated
  if (textWidth > maxWidth && type !== LabelChipType.Dropdown) {
    return <CustomTooltip title={label.title}>{chipContent}</CustomTooltip>;
  }

  return chipContent;
};

function getChipMaxWidth(type: LabelChipType): number {
  switch (type) {
    case LabelChipType.Table:
      return 100;
    case LabelChipType.TableHeader:
      return 300;
    case LabelChipType.Dropdown:
      return 140;
    case LabelChipType.TaskCard:
      return 100;
    case LabelChipType.Modal:
      return 180;
    default:
      return 80;
  }
}

function getTextWidth(text: string, font: string): number {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 0;
  context.font = font;
  return context.measureText(text).width;
}

export default LabelChip;
